import { zodResolver } from "@hookform/resolvers/zod";
import {
  AnalyticEvent,
  useCheckIfProfileExistsLazyQuery,
  useGetUserProfileLazyQuery,
  usePostHog,
} from "@stepapp/core";
import { RegisterTypeEnum } from "@stepapp/core/apiTypes";
import {
  Block,
  BottomSheet,
  Button,
  Divider,
  Input,
  ProgressBar,
  palette,
  SvgIcon,
  spacing,
  Touchable,
  Typography,
} from "@stepapp/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet } from "react-native";
import { focusInput } from "../../helpers";
import { useAppDispatch } from "../../hooks/redux";
import useSignUp from "../../hooks/useSignUp";
import { useRegisterFormSchema } from "../../schemas";
import { setUserData } from "../../store/user";
import OneTimePasswordForm from "../OneTimePasswordForm";
import { ServiceSceneDescription } from "../Service/ServiceSceneHeader";

type Props = {
  loginType: string;
  phoneNumber: string;
  toggle: () => void;
};

export type LoginFormValues = {
  email: string;
  firstName: string;
  lastName: string;
  code: string;
  otpCodeVerified: boolean;
  phoneNumber: string;
};

const defaultValues = {
  email: "",
  firstName: "",
  lastName: "",
  code: "",
  otpCodeVerified: false,
  phoneNumber: "",
};

const LoginModal = ({ loginType, phoneNumber, toggle }: Props) => {
  const { t } = useTranslation();
  const postHog = usePostHog();
  const lastNameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const isRegister = loginType === "register";
  const [isLoading, setIsLoading] = useState(false);

  const signUp = useSignUp();
  const dispatch = useAppDispatch();
  const [checkIfProfileExists] = useCheckIfProfileExistsLazyQuery();
  const [getUserProfile] = useGetUserProfileLazyQuery({
    variables: {
      isCustomerProfile: true,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [questionNumber, setQuestionNumber] = useState(1);
  const isFirstQuestion = questionNumber === 1;
  const isSecondQuestion = questionNumber === 2;

  const registerSchema = useRegisterFormSchema({ isRegister: isSecondQuestion });

  const {
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { isValid, errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(registerSchema) as Resolver<LoginFormValues>,
    defaultValues,
  });

  const values = watch();
  const disabled = useMemo(() => !isValid, [isValid]);

  const handleButtonPress = useCallback(() => {
    if (isFirstQuestion) {
      toggle();
    } else {
      setQuestionNumber(1);
    }
  }, [isFirstQuestion, toggle]);

  const handleLoginVerification = useCallback(() => {
    if (isRegister) {
      setQuestionNumber(2);
    }
  }, [isRegister]);

  const onSubmit = useCallback(
    async ({ email, firstName, lastName, phoneNumber }: LoginFormValues) => {
      setIsLoading(true);
      const successSignUp = await signUp({
        email,
        lastName,
        firstName,
        phoneNumber,
        registerType: RegisterTypeEnum.Email,
      });

      if (successSignUp) {
        postHog?.capture(AnalyticEvent.Register.Success);
        toggle();
      }
      setIsLoading(false);
    },
    [postHog, signUp, toggle],
  );

  const handleClose = useCallback(() => {
    toggle();
    setQuestionNumber(1);
  }, [toggle]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (phoneNumber) {
      setValue("phoneNumber", phoneNumber);
    }
  }, [phoneNumber]);

  useEffect(() => {
    const fetchData = async () => {
      const { otpCodeVerified, code } = values;

      if (phoneNumber && otpCodeVerified && code) {
        setValue("otpCodeVerified", false);
        setIsLoading(true);

        try {
          const { data: profileExists } = await checkIfProfileExists();
          const newUser = isRegister || !profileExists?.checkIfProfileExists;

          if (newUser) {
            postHog?.capture(AnalyticEvent.Register.Details);
            setQuestionNumber(2);
          } else {
            const { data: getUserData } = await getUserProfile();
            getUserData && dispatch(setUserData(getUserData?.getMyProfile));

            toggle();
          }
        } catch (_error) {
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [
    values,
    values.code,
    values.otpCodeVerified,
    phoneNumber,
    isRegister,
    toggle,
    dispatch,
    checkIfProfileExists,
    getUserProfile,
    setValue,
    postHog,
  ]);

  return (
    <BottomSheet
      avoidKeyboard
      contentStyle={styles.containerServiceSummary}
      enableScroll={false}
      toggle={handleClose}
      visible={!!loginType}
    >
      <Touchable onPress={() => handleButtonPress()}>
        <Block
          alignCenter
          justifyCenter
          style={isFirstQuestion ? styles.closeIcon : styles.backIcon}
        >
          <SvgIcon
            color={isFirstQuestion ? palette.secondary.grey : palette.common.black}
            name={isFirstQuestion ? "cross" : "chevron-left"}
            size={isFirstQuestion ? 44 : 28}
          />
        </Block>
      </Touchable>

      <ScrollView
        contentContainerStyle={styles.contentContainer}
        keyboardShouldPersistTaps="always"
      >
        {questionNumber === 1 && (
          <OneTimePasswordForm
            setLoading={setIsLoading}
            loading={isLoading}
            handleLoginVerification={handleLoginVerification}
            phoneNumber={phoneNumber}
            setValue={setValue}
          />
        )}
        {questionNumber === 2 && (
          <Block marginBottom={0.5} marginTop={2}>
            <Block marginBottom={2.5}>
              <ProgressBar cap={3} value={2} />
              <Block paddingTop={3}>
                <Typography variant="largeTitle">{t("screens.myHome.addInfo")}</Typography>
              </Block>

              <ServiceSceneDescription description={t("screens.login.noAccRegister")} />
            </Block>
            <Input
              error={errors?.firstName?.message}
              onBlur={() => trigger("firstName")}
              onChangeText={(value: string) =>
                setValue("firstName", value, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                })
              }
              onSubmitEditing={focusInput(lastNameInputRef)}
              placeholder={t("components.input.placeholder.firstName")}
              returnKeyType="next"
              testID="firstNameInput"
              value={values.firstName}
            />
            <Input
              error={errors?.lastName?.message}
              onBlur={() => trigger("lastName")}
              onChangeText={(value: string) =>
                setValue("lastName", value, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                })
              }
              onSubmitEditing={focusInput(emailInputRef)}
              placeholder={t("components.input.placeholder.lastName")}
              ref={lastNameInputRef}
              returnKeyType="next"
              testID="lastNameInput"
              value={values.lastName}
            />
            <Input
              autoCapitalize="none"
              autoComplete="email"
              error={errors?.email?.message}
              inputMode="email"
              keyboardType="email-address"
              onBlur={() => trigger("email")}
              onChangeText={(value: string) =>
                setValue("email", value, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                })
              }
              onSubmitEditing={handleSubmit(onSubmit)}
              placeholder={t("components.input.placeholder.email")}
              ref={emailInputRef}
              returnKeyType="done"
              testID="emailInput"
              value={values.email}
            />
          </Block>
        )}
      </ScrollView>

      {isSecondQuestion && (
        <>
          <Divider spaceless color={palette.secondary.grey} style={styles.divider} />
          <Button
            disabled={disabled || isLoading}
            label={isRegister ? t("screens.auth.register") : t("common.continue")}
            loading={isLoading}
            onPress={handleSubmit(onSubmit)}
            style={styles.continueButton}
            type="primary"
          />
        </>
      )}
    </BottomSheet>
  );
};

export default LoginModal;

const styles = StyleSheet.create({
  backIcon: {
    backgroundColor: palette.secondary.grey,
    borderRadius: spacing.unit * 2.5,
    height: spacing.unit * 5,
    width: spacing.unit * 5,
  },
  closeIcon: {
    backgroundColor: palette.common.black,
    borderRadius: 20,
    height: spacing.unit * 4.5,
    width: spacing.unit * 4.5,
  },
  containerServiceSummary: {
    marginTop: 150,
    padding: spacing.unit * 2.5,
  },
  contentContainer: { marginBottom: spacing.unit },
  continueButton: {
    marginBottom: spacing.unit,
    marginTop: spacing.unit * 2,
  },
  divider: {
    marginHorizontal: spacing.unit * -3,
  },
});
